import styled from 'styled-components'
import * as BsIcons from 'react-icons/bs'

// Common styled components used across table components

export const TableList = styled.ol`
    padding: 0px;
    margin: 0px;
    list-style: none;
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
    background-color: ${({ theme }) => theme.table.background};
    @media (max-width: ${({ theme }) => theme.breakpoint.xs}) {
        display: ${({ $showOnMobile }) => $showOnMobile ? 'block' : 'none'};
    }
`

export const TableListMobile = styled.ol`
    padding: 0px;
    margin: 0px;
    list-style: none;
    border-top-right-radius: 0.5rem;
    border-top-left-radius: 0.5rem;
    background-color: ${({ theme }) => theme.table.background};
    @media (min-width: ${({ theme }) => theme.breakpoint.xs}) {
        display: none;
    }
`

/*
 * `--table-row-weight` is how a row is emphasised. A list can declare
 * `emphasizeLine: { condition }` and the rows that satisfy it are set in bold —
 * a chart of accounts does it for its headings and its sum lines, so the shape
 * of the chart is readable without opening anything.
 *
 * It travels as a custom property rather than a prop because the text it has to
 * reach sits two components down, inside TableCell and the mobile tags, and a
 * font-weight written there would otherwise tie with the row's own rule on
 * specificity and be decided by whichever stylesheet happened to be injected
 * last. Inheritance has no such argument.
 */
const rowWeight = ({ $emphasis }) => $emphasis ? '--table-row-weight: 700;' : ''

export const TableListItem = styled.div`
    ${rowWeight}
    display: flex;
    flex: 1 0 auto;
    min-width: 0px;
    opacity: 1;
    min-height: 56px;
    position: relative;
    border-top: 1px solid ${({ theme }) => theme.table.borderTop};
    -webkit-box-align: center;
    align-items: start;
    color: ${({ theme }) => theme.font.color};
    cursor: pointer;
    padding: 16px 24px;
    text-decoration: none;
    background-color: ${({ $isSelected, theme }) => 
        $isSelected ? theme.table.active : 'transparent'
    };
    &:hover{
        background-color: ${({ theme }) => theme.table.active};
    }
`

export const TableListItemMobile = styled.li`
    ${rowWeight}
    min-width: 0px;
    opacity: 1;
    min-height: 56px;
    position: relative;
    border-top: 1px solid ${({ theme }) => theme.table.borderTop};
    -webkit-box-align: center;
    align-items: center;
    color: ${({ theme }) => theme.font.color};
    cursor: pointer;
    padding: 0.5rem;
    background-color: ${props => props.$isChild ? props.theme.table.childBackground || 'rgba(0,0,0,0.02)' : 'transparent'};
    padding-left: ${props => props.$isChild ? '2rem' : '1rem'};
`

export const ActionsOverlay = styled.div`
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
`

export const TableCell = styled.div`
    box-sizing: border-box;
    flex: 0 1 ${(props) => (props.$len) || 'auto'};
    flex-shrink: ${(props) => (props.$shrink) || 1};
    min-width: 0px;
    display: flex;
    margin-right: 24px;
    -webkit-box-align: center;
    align-items: center;
    -webkit-box-pack: start;
    justify-content: flex-start;
    overflow: hidden;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    p {
        margin:0;
        font-size: var(--font-XS);
        line-height: 24px;
        display: block;
        color: ${({ theme }) => theme.font.color};
        font-family: "Euclid Circular B", sans-serif;
        font-weight: var(--table-row-weight, 500);
        text-align: inherit;
        user-select: auto;
        transition-property: color;
        transition-duration: 0.1s;
        transition-timing-function: ease-out;
        text-decoration: none;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
        white-space: normal;
        max-width: 100%;
        span {
            display: inline-block;
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;
            white-space: normal;
            max-width: 100%;
        }
    }

    svg{
        margin-right:  ${(props) => (props.$svgMargin) ? props.$svgMargin : '0.5rem'};
    }
`

// Mobile-specific styled components
export const CardWrapper = styled.div`
    display: grid;
    grid-template-columns: ${props => props.$hasActions ? '2fr auto auto' : '2fr auto'};
    gap: 0.5rem;
    align-items: center;
    p {
        margin:0;
        display: block;
        color: ${({ theme }) => theme.font.color};
        user-select: auto;
        transition-property: color;
        transition-duration: 0.1s;
        transition-timing-function: ease-out;
    }
    svg {
        margin-right: 0.5rem;
    }
`

export const Tags = styled.div`
    display: inline;
`

export const Flex = styled.div`
    display: flex;
    align-items: center;
`

export const LeftTag = styled.p`
    font-size: var(--font-S);
    font-weight: 800;
`

export const LeftSubTag = styled.p`
    font-size: var(--font-XS);
    font-weight: var(--table-row-weight, 500);
`

export const RightTag = styled.p`
    font-size: var(--font-S);
    font-weight: 800;
    text-align: right;
`

export const RightSubTag = styled.p`
    font-size: var(--font-XS);
    font-weight: var(--table-row-weight, 500);
    text-align: right;
`

export const IconWrapper = styled.div`
    padding: ${props => props.$padding}; 
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
`

// Common NoData component
export const NoData = (
    <TableList>
        <TableListItem>
            <TableCell>
                <BsIcons.BsExclamationOctagonFill />
                <p>No data available.</p>
            </TableCell>
        </TableListItem>
    </TableList>
)

export const NoDataMobile = (
    <TableListMobile>
        <TableListItemMobile>
            <CardWrapper>
                <Flex>
                    <IconWrapper>
                        <BsIcons.BsExclamationOctagonFill />
                    </IconWrapper>
                    <p>No data available.</p>
                </Flex>
            </CardWrapper>
        </TableListItemMobile>
    </TableListMobile>
)

// Common utility function
export const createSlugPath = (slug) => {
    return slug !== '' ? `/?state=${slug}` : ''
}

// Common data validation function
export const hasValidData = (result) => {
    return result.data && result.data.pages.length > 0 && Array.isArray(result.data.pages[0]?.data)
}
